// In production, nginx rewrites /assess/api/* → /api/* before forwarding to port 4000.
// In dev, vite proxy handles /assess/api/* → localhost:4000/api/*.
const API_BASE = '/assess/api';

export interface ApiError extends Error {
  status?: number;
  /** Stable backend error code, e.g. `ATTEMPT_LOCKED`. */
  errorCode?: string;
  /** When `errorCode` is `ATTEMPT_*`, the takeover URL the player can POST to. */
  takeoverUrl?: string;
  attemptId?: string;
  startedAt?: string;
}

class ApiClient {
  private getAuthHeader(): Record<string, string> {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private getAcademyHeader(): Record<string, string> {
    const academyId = sessionStorage.getItem('academy_id');
    return academyId ? { 'X-Academy-ID': academyId } : {};
  }

  async fetch<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers = {
      'Content-Type': 'application/json',
      ...this.getAuthHeader(),
      ...this.getAcademyHeader(),
      ...(options.headers as Record<string, string> || {}),
    };

    const response = await fetch(`${API_BASE}${path}`, { ...options, headers });

    if (response.status === 401) {
      // Parse the body once so we can branch on error_code AND surface the
      // human message if needed.
      const errBody = await response.clone().json().catch(() => ({} as any));

      // Single-session kick: don't try to refresh — the server has explicitly
      // invalidated this session because the user signed in elsewhere.
      if (errBody?.error_code === 'SESSION_REVOKED') {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/assess/login?reason=signed_in_elsewhere';
        throw new Error('Signed in on another device');
      }

      // Only attempt refresh if we actually have a refresh token.
      const hasRefreshToken = !!localStorage.getItem('refresh_token');
      if (!hasRefreshToken) {
        throw new Error(errBody.error || 'Unauthorized');
      }

      const refreshResult = await this.tryRefresh();
      if (refreshResult === 'ok') {
        const retryHeaders = {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
          ...this.getAcademyHeader(),
        };
        const retryResponse = await fetch(`${API_BASE}${path}`, { ...options, headers: retryHeaders });
        if (!retryResponse.ok) {
          const error = await retryResponse.json().catch(() => ({ error: 'Request failed' }));
          const err = new Error(error.error || 'Request failed');
          (err as ApiError).status = retryResponse.status;
          throw err;
        }
        const retryText = await retryResponse.text();
        if (!retryText) return undefined as T;
        return JSON.parse(retryText) as T;
      } else {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        const reason = refreshResult === 'kicked' ? 'signed_in_elsewhere' : 'expired';
        window.location.href = `/assess/login?reason=${reason}`;
        throw new Error(refreshResult === 'kicked' ? 'Signed in on another device' : 'Session expired');
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      const err = new Error(error.error || `HTTP ${response.status}`);
      (err as ApiError).status = response.status;
      (err as ApiError).errorCode = error.error_code;

      // The 409 ATTEMPT_IN_PROGRESS_ELSEWHERE response is JSON-in-string —
      // the backend stringifies a JSON object as the `error` message. Try to
      // parse it back so the player UI can render a "take over" dialog.
      if (
        response.status === 409 &&
        typeof error.error === 'string'
      ) {
        try {
          const inner = JSON.parse(error.error);
          if (inner?.error_code === 'ATTEMPT_IN_PROGRESS_ELSEWHERE') {
            (err as ApiError).errorCode = inner.error_code;
            (err as ApiError).takeoverUrl = inner.takeover_url;
            (err as ApiError).attemptId = inner.attempt_id;
            (err as ApiError).startedAt = inner.started_at;
          }
        } catch {
          // Not JSON — leave as-is.
        }
      }
      // 423 ATTEMPT_LOCKED carries the takeover URL in the standard shape.
      if (response.status === 423 && error.error_code === 'ATTEMPT_LOCKED') {
        (err as ApiError).errorCode = 'ATTEMPT_LOCKED';
        // Backend doesn't currently include attempt_id in the 423 body, but
        // the caller knows it (it's the route param), so we don't need it.
      }

      throw err;
    }

    const text = await response.text();
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }

  /** Take over an in-progress attempt that's locked to another device. */
  async takeoverAttempt(attemptId: string): Promise<void> {
    await this.post<void>(`/student/assessment-attempts/${attemptId}/takeover`, {});
  }

  private async tryRefresh(): Promise<'ok' | 'kicked' | 'failed'> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return 'failed';
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!response.ok) {
        if (response.status === 401) {
          const body = await response.clone().json().catch(() => ({} as any));
          if (body?.error_code === 'SESSION_REVOKED') return 'kicked';
        }
        return 'failed';
      }
      const data = await response.json();
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      return 'ok';
    } catch {
      return 'failed';
    }
  }

  get<T>(path: string) { return this.fetch<T>(path, { method: 'GET' }); }
  post<T>(path: string, body: unknown) { return this.fetch<T>(path, { method: 'POST', body: JSON.stringify(body) }); }
  put<T>(path: string, body: unknown) { return this.fetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }); }
  patch<T>(path: string, body: unknown) { return this.fetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }); }
  delete<T>(path: string) { return this.fetch<T>(path, { method: 'DELETE' }); }

  /** Upload a file with FormData (no JSON Content-Type) */
  async upload<T>(path: string, formData: FormData): Promise<T> {
    const headers = { ...this.getAuthHeader(), ...this.getAcademyHeader() };
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  /** Get a raw Blob response (for file downloads/exports) */
  async blob(path: string): Promise<Blob> {
    const headers = { ...this.getAuthHeader(), ...this.getAcademyHeader() };
    const response = await fetch(`${API_BASE}${path}`, { headers });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.blob();
  }

  /** Get the current auth token */
  getToken(): string | null {
    return localStorage.getItem('access_token');
  }
}

export const apiClient = new ApiClient();
